import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '@photosi/rn-components/modals/modal';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { useStore } from 'src/contexts/StoreContext';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { sharedStyle, styleMobile } from './ModalUploadMultisource.style';
import { observer } from 'mobx-react-lite';
import { TestIDs } from 'src/constants/testIDs.constants';
import { DynamicModalProps } from '../DynamicModal.types';
import { ModalUploadMultiSourceDesktop } from './ModalUploadMultiSourceDesktop';
import { ModalUploadMultiSourceMobile } from './ModalUploadMultiSourceMobile';
import { DiscountScale } from '@photosi/expo-photosi-sdk/build/Project';
import { useNetworkState } from 'src/editor-v3/hooks/useNetworkState';
import { LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { useStepper } from 'src/editor-v3/hooks/useStepper';
import { icon } from 'src/theme/colors';
import { ActivityIndicator, View } from 'react-native';

enum STEPS {
  Format = 'Format',
  Picker = 'Picker',
}

export const StepsToComplete = [STEPS.Format, STEPS.Picker];
export const StepsToCompleteFormatSkip = [STEPS.Picker];

export const ModalUploadMultiSource = observer((props: DynamicModalProps) => {
  const {
    project: {
      activeProject,
      setShowUploadPhotosBanner,
      startShareObserver,
      stopShareObserver,
      getMinDiscountScales,
      groupCode,
      activeGroup,
      productConfigurationId,
      getFrameConfigStepsData,
      setProductConfigurationId,
    },
    i18n,
    dynamicModal: { closeModal },
  } = useStore();
  const {
    type,
    dynamicModalVisible,
    dynamicModalLayer: { id, groupCode: groupCodeDynamic, isReplacingPhoto },
  } = props;

  const { isDesktop } = useResponsiveScreen();
  const options = [
    { label: i18n.t('modal-picker-my-photos:black'), value: 'BLACK' },
    { label: i18n.t('modal-picker-my-photos:white'), value: 'WHITE' },
  ];
  const [discount, setDiscount] = useState<DiscountScale[]>([]);
  const isOnline = useNetworkState();
  const [data, setData] = useState<LookupChoiceForStep[] | undefined>(isReplacingPhoto ? [] : undefined);
  const [selectedCard, setSelectedCard] = useState<string | undefined>(undefined);
  const initialOption = options[0].value;
  const [selectedOption, setSelectedOption] = useState(initialOption);
  const getConfiguration = useMemo(() => {
    return {
      stepsToComplete: !!data?.length ? StepsToComplete : StepsToCompleteFormatSkip,
      initialStep: !!data?.length ? STEPS.Format : STEPS.Picker,
    };
  }, [data]);

  const { step, goToNextStep, goBack, isLastStep } = useStepper<STEPS>(getConfiguration);
  const backgroundImageUrl = data?.find(
    item => item.groupingStep === selectedOption && item.name === selectedCard,
  )?.backgroundImageUrl;

  const getCards = useMemo(() => {
    return data?.filter(item => item.groupingStep === selectedOption) ?? [];
  }, [data, selectedOption]);

  const onConfirmStep = useCallback(() => {
    if (!isLastStep) {
      const prodConfId = data?.find(item => item.groupingStep === selectedOption && item.name === selectedCard);
      prodConfId && setProductConfigurationId(prodConfId.productConfigurationId);
      goToNextStep();
      return;
    }
  }, [isLastStep, selectedCard, selectedOption]);

  useEffect(() => {
    if (!!data?.length && !selectedCard) {
      const prodConfId = data?.find(item => item.isDefault);
      setSelectedCard(prodConfId?.name);
    }
  }, [data, selectedCard]);

  useEffect(() => {
    if (activeProject?.hasShareToken) {
      stopShareObserver();
      startShareObserver();
    }

    return () => {
      stopShareObserver();
    };
  }, [activeProject?.hasShareToken]);

  useEffect(() => {
    const code = groupCodeDynamic ? groupCodeDynamic : activeGroup ? activeGroup.code : groupCode;
    if (code) {
      getMinDiscountScales(code).then(setDiscount);
    }

    if (!isReplacingPhoto && productConfigurationId) {
      // load data from api
      getFrameConfigStepsData(productConfigurationId)
        .then(setData)
        .catch(() => {
          setData([]);
        });
    }
  }, []);

  const handleOnClose = () => {
    setShowUploadPhotosBanner(false);
    closeModal();
    activeProject?.deselectAllThumbnailImages();
    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.closeClick);
  };

  const ModalUploadMultiSource = isDesktop ? ModalUploadMultiSourceDesktop : ModalUploadMultiSourceMobile;
  const isModalShown = typeof data !== 'undefined' && dynamicModalVisible;

  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (isModalShown) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    } else {
      setIsLoading(true);
    }
  }, [isModalShown]);

  return (
    <>
      {isLoading && (
        <View style={sharedStyle.loadingContainer}>
          <ActivityIndicator size="large" color={icon.iconInteractive} />
        </View>
      )}
      <Modal
        title={i18n.t('upload:heading')}
        visible={isModalShown}
        onClose={handleOnClose}
        testID={TestIDs.CHOOSE_PHOTO}
        style={!isDesktop ? styleMobile.containerMobile : { maxWidth: 1200 }}
        style_container={!isDesktop ? { height: '100%' } : undefined}
        showHeader={false}
        type={type}
        id={id}
        isInstant={!isDesktop}
      >
        <ModalUploadMultiSource
          {...props}
          discount={discount}
          isOnline={isOnline}
          getCards={getCards}
          goBack={goBack}
          initialOption={initialOption}
          onConfirmStep={onConfirmStep}
          options={options}
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
          setSelectedOption={setSelectedOption}
          step={step}
          backgroundImageUrl={backgroundImageUrl}
        />
      </Modal>
    </>
  );
});
